import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthShell } from '@/components/auth/AuthShell';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { FieldError } from '@/components/ui/FieldError';
import { useAuth } from '@/hooks/useAuth';
import { loginSchema, type LoginInput } from '@/types/schemas';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const [submitError, setSubmitError] = useState('');
  // Флаг ставит обработчик истёкшей сессии в useAuth. Читаем один раз (без
  // удаления на mount — иначе StrictMode-перемонтаж потеряет его); чистим
  // после успешного входа.
  const [sessionExpired] = useState(() => {
    try {
      return !!sessionStorage.getItem('ef_session_expired');
    } catch {
      return false;
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = handleSubmit(async (data) => {
    setSubmitError('');
    const res = await login(data.email.trim(), data.password);
    if (res.ok) {
      try {
        sessionStorage.removeItem('ef_session_expired');
      } catch {
        // ignore
      }
      navigate(from, { replace: true });
    } else {
      setSubmitError(res.message ?? 'Invalid email or password');
    }
  });

  return (
    <AuthShell
      leftHeading={
        <>
          Train
          <br />
          hard.
          <br />
          Win.
        </>
      }
      formHeader={
        <>
          <h2>Welcome back</h2>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--dim)',
              marginTop: 4,
            }}
          >
            Log in to continue
          </p>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {sessionExpired && (
          <div
            role="status"
            style={{
              color: 'var(--dim)',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              padding: '10px 12px',
              marginBottom: 12,
              borderRadius: 8,
              border: '1px solid var(--border, rgba(255,255,255,0.12))',
            }}
          >
            Your session expired. Please log in again.
          </div>
        )}
        <div className="form-group">
          <label htmlFor="username">Email</label>
          <div className="input-wrapper">
            <Mail size={20} strokeWidth={2} />
            <input
              type="email"
              id="username"
              className="form-input"
              placeholder="your@email.com"
              autoComplete="email"
              aria-invalid={!!errors.email}
              {...register('email')}
            />
          </div>
          {errors.email && <FieldError message={errors.email.message} />}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <PasswordInput
            id="password"
            placeholder="Enter your password"
            autoComplete="current-password"
            registration={register('password')}
            invalid={!!errors.password}
          />
          {errors.password && <FieldError message={errors.password.message} />}
        </div>

        {submitError && <FieldError message={submitError} />}

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Logging in…' : 'Log in →'}
        </button>

        <div className="divider">
          <span>or</span>
        </div>

        <GoogleButton label="Continue with Google" />

        <div className="footer-text">
          No account? <Link to="/register">Sign up →</Link>
        </div>
      </form>
    </AuthShell>
  );
}
